'use client';
import React, { useState } from 'react';
import Link from 'next/link';
import { Star, MapPin, Languages, Shield, ChevronRight, Clock } from 'lucide-react';

const priests = [
  {
    id: 1,
    name: 'Pandit Raghavendra Sharma',
    shortName: 'P. R. Sharma',
    sampradaya: 'Smartha',
    experience: '28 yrs',
    location: 'Bengaluru, KA',
    languages: ['Sanskrit', 'Kannada', 'Telugu', 'Hindi'],
    rating: 4.9,
    reviews: 847,
    rituals: 42,
    specialization: 'Gruhapravesam, Vivaha, Homa',
    available: true,
    nextSlot: 'Tomorrow, 7 AM',
    price: '₹2,500',
    badges: ['Verified', 'Temple Associated', 'Senior Priest'],
    avatar: '🕉️',
    avatarBg: 'from-amber-100 to-orange-100',
    completedRituals: 1240,
  },
  {
    id: 2,
    name: 'Acharya Venkateshwara Iyengar',
    shortName: 'A. V. Iyengar',
    sampradaya: 'Vaishnava',
    experience: '35 yrs',
    location: 'Chennai, TN',
    languages: ['Sanskrit', 'Tamil', 'Telugu'],
    rating: 4.8,
    reviews: 1203,
    rituals: 58,
    specialization: 'Satyanarayan, Navagraha, Vishnu Pooja',
    available: true,
    nextSlot: 'Today, 6 PM',
    price: '₹3,200',
    badges: ['Verified', 'Tirupati Trained', 'Senior Priest'],
    avatar: '🌺',
    avatarBg: 'from-rose-100 to-pink-100',
    completedRituals: 2180,
  },
  {
    id: 3,
    name: 'Pandit Subrahmanya Dikshitar',
    shortName: 'P. S. Dikshitar',
    sampradaya: 'Shaiva',
    experience: '22 yrs',
    location: 'Hyderabad, TS',
    languages: ['Sanskrit', 'Telugu', 'Hindi'],
    rating: 4.9,
    reviews: 634,
    rituals: 38,
    specialization: 'Rudra Abhishekam, Shiva Pooja, Havan',
    available: false,
    nextSlot: 'Mar 22, 8 AM',
    price: '₹2,800',
    badges: ['Verified', 'Vedic Scholar'],
    avatar: '🔱',
    avatarBg: 'from-slate-100 to-gray-100',
    completedRituals: 890,
  },
  {
    id: 4,
    name: 'Pandit Krishnamurthy Bhatt',
    shortName: 'P. K. Bhatt',
    sampradaya: 'Smartha',
    experience: '18 yrs',
    location: 'Mumbai, MH',
    languages: ['Sanskrit', 'Marathi', 'Hindi', 'Gujarati'],
    rating: 4.7,
    reviews: 412,
    rituals: 31,
    specialization: 'Ganesh Pooja, Satyanarayan, Vastu',
    available: true,
    nextSlot: 'Today, 9 AM',
    price: '₹1,800',
    badges: ['Verified', 'Background Checked'],
    avatar: '🐘',
    avatarBg: 'from-yellow-100 to-amber-100',
    completedRituals: 670,
  },
];

const sampradayaColors: Record<string, string> = {
  Smartha: 'bg-amber-100 text-amber-800 border-amber-200',
  Vaishnava: 'bg-rose-100 text-rose-800 border-rose-200',
  Shaiva: 'bg-slate-100 text-slate-700 border-slate-200',
};

export default function FeaturedPriests() {
  const [hoveredId, setHoveredId] = useState<number | null>(null);

  return (
    <section className="py-16 px-4 sm:px-6 bg-gradient-to-b from-ivory to-cream">
      <div className="max-w-screen-xl mx-auto">
        {/* Section Header */}
        <div className="flex flex-col sm:flex-row sm:items-end justify-between mb-12 gap-4">
          <div>
            <span className="inline-block text-xs font-semibold uppercase tracking-widest text-saffron mb-3">
              Trusted Priests
            </span>
            <h2 className="font-display text-3xl sm:text-4xl font-bold text-maroon mb-2">
              Featured Priests
            </h2>
            <p className="text-maroon/60 text-base font-body">
              Verified, experienced, and sampradaya-certified
            </p>
            <div className="mt-3 w-16 h-0.5 gold-gradient rounded-full" />
          </div>
          <Link
            href="/priests"
            className="inline-flex items-center gap-2 text-saffron font-medium text-sm hover:gap-3 transition-all"
          >
            View all 840+ priests
            <ChevronRight size={16} />
          </Link>
        </div>

        {/* Priest Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {priests.map((priest) => (
            <Link
              key={priest.id}
              href={`/priests/${priest.id}`}
              onMouseEnter={() => setHoveredId(priest.id)}
              onMouseLeave={() => setHoveredId(null)}
              className={`group relative bg-white rounded-3xl border overflow-hidden transition-all duration-300 cursor-pointer ${
                hoveredId === priest.id
                  ? 'border-gold/50 shadow-2xl shadow-gold/10 -translate-y-1'
                  : 'border-gold/15 shadow-md'
              }`}
            >
              {/* Top gradient bar */}
              <div className="h-1.5 w-full saffron-gradient" />

              <div className="p-5">
                {/* Avatar & Availability */}
                <div className="flex items-start justify-between mb-4">
                  <div className="relative">
                    <div
                      className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${priest.avatarBg} flex items-center justify-center text-3xl shadow-md border border-gold/20`}
                    >
                      {priest.avatar}
                    </div>
                    <div
                      className={`absolute -bottom-1 -right-1 w-4 h-4 rounded-full border-2 border-white ${
                        priest.available ? 'bg-emerald-400' : 'bg-gray-300'
                      }`}
                    />
                  </div>
                  <span className={`text-xs font-medium px-2.5 py-1 rounded-full border ${sampradayaColors[priest.sampradaya]}`}>
                    {priest.sampradaya}
                  </span>
                </div>

                {/* Name & Location */}
                <h3 className="font-display text-base font-semibold text-maroon leading-tight mb-1">
                  {priest.name}
                </h3>
                <div className="flex items-center gap-1 text-maroon/50 text-xs mb-3">
                  <MapPin size={11} />
                  <span>{priest.location}</span>
                  <span className="mx-1">·</span>
                  <span>{priest.experience} exp.</span>
                </div>

                {/* Rating */}
                <div className="flex items-center gap-2 mb-3">
                  <div className="flex items-center gap-1">
                    {[1, 2, 3, 4, 5].map((s) => (
                      <Star
                        key={s}
                        size={12}
                        className={s <= Math.floor(priest.rating) ? 'star-filled' : 'star-empty'}
                        fill={s <= Math.floor(priest.rating) ? '#D4AF37' : 'none'}
                        stroke="#D4AF37"
                      />
                    ))}
                  </div>
                  <span className="text-xs font-semibold text-maroon tabular-nums">{priest.rating}</span>
                  <span className="text-xs text-maroon/40">({priest.reviews})</span>
                </div>

                {/* Languages */}
                <div className="flex items-center gap-1 flex-wrap mb-4">
                  <Languages size={11} className="text-sandalwood flex-shrink-0" />
                  {priest.languages.slice(0, 3).map((lang) => (
                    <span key={lang} className="text-[10px] text-maroon/60 bg-cream px-1.5 py-0.5 rounded">
                      {lang}
                    </span>
                  ))}
                  {priest.languages.length > 3 && (
                    <span className="text-[10px] text-maroon/40">+{priest.languages.length - 3}</span>
                  )}
                </div>

                {/* Specialization */}
                <p className="text-xs text-maroon/60 mb-4 line-clamp-2 font-body">{priest.specialization}</p>

                {/* Badges */}
                <div className="flex flex-wrap gap-1 mb-4">
                  {priest.badges.slice(0, 2).map((badge) => (
                    <span key={badge} className="inline-flex items-center gap-1 text-[10px] font-medium text-emerald-700 bg-emerald-50 border border-emerald-200 px-2 py-0.5 rounded-full">
                      <Shield size={9} />
                      {badge}
                    </span>
                  ))}
                </div>

                {/* Next Slot & Price */}
                <div className="flex items-center justify-between pt-3 border-t border-gold/15">
                  <div className="flex items-center gap-1 text-xs text-maroon/60">
                    <Clock size={11} />
                    <span>{priest.nextSlot}</span>
                  </div>
                  <span className="text-sm font-bold text-maroon tabular-nums">{priest.price}</span>
                </div>
              </div>

              {/* Book Now on Hover */}
              <div
                className={`px-5 pb-5 transition-all duration-300 ${
                  hoveredId === priest.id ? 'opacity-100 max-h-16' : 'opacity-0 max-h-0 overflow-hidden'
                }`}
              >
                <div className="saffron-gradient text-white text-sm font-semibold py-2.5 rounded-xl text-center btn-glow">
                  Book Now
                </div>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}